/* eslint-disable no-console */
// @ts-nocheck

import * as L from "leaflet";
import { CirclePicker } from "react-color";
import { Link } from "react-router-dom";
import { ReactNode, useEffect, useRef, useState } from "react";
import { MapAddress, MapTextBox } from "interfaces/interfaces";
import { MAP_MODE } from "types/types";
import Line from "assets/images/line-segment.svg";
import Marker from "assets/images/map-marker.svg";
import Polygon from "assets/images/shape-square.svg";
import clearMapIcon from "assets/images/clear-map.svg";
import undo from "assets/images/undo.svg";
import perimeter from "assets/images/perimeter.svg";
import freeDrawPolygon from "assets/images/freedraw-polygon.svg";
import freeDrawPerimeter from "assets/images/freedraw-perimeter.svg";
import { ProductMeasurementJob } from "generated/graphql";
import { Icon } from "@mui/material";
import "./Map.css";

interface Props {
  map: L.Map;
  featureGroup: L.FeatureGroup | null;
  mapMode: MAP_MODE | null;
  currentDrawShape: L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline" | null;
  color: string;
  name: string;
  setColor: (color: string) => void;
  setName: (name: string) => void;
  isEditing: boolean;
  onSave: () => void;
  showSave: boolean;
  isDragging: boolean;
  toggleDrag: () => void;
  deleteLayer: () => void;
  total: {
    area: {
      feet: number;
      yards: number;
    };
    perimeter: {
      feet: number;
      yards: number;
    };
  };
  showLineLengthsWhileMeasuring: boolean;
  setShowLineLengthsWhileMeasuring: (showLineLengthsWhileMeasuring: boolean) => void;
  showLineLength: boolean;
  setShowLineLength: (showLineLength: boolean) => void;
  showTooltip: boolean;
  setShowTooltip: (showTooltip: boolean) => void;
  editingLayer: object;
  editingLayerType: string | null;
  toggleDrawMode: (
    map: L.Map,
    drawShape: L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline",
    layerColor: string,
    isEditing: boolean,
    currentDrawShape: string | null
  ) => void;
  isTouch: boolean;
  selectPlace: (place: any) => void;
  clearMap: () => void;
  createTextBox: () => void;
  textToolActive: boolean;
  selectedTextBox: MapTextBox | null;
  updateSelectedTextBox: (updates: Partial<MapTextBox>) => void;
  takeScreenshot: () => void;
  isGeneratingScreenshot: boolean;
  selectedAddress: MapAddress | null;
  hideLocation?: boolean;
  goBack?: boolean;
  goBackTo?: string;
  goBackLabel?: string;
  showTools?: [keyof typeof ProductMeasurementJob];
  close?: () => void;
  undoLastDrawPoint: () => void;
  canUndo: boolean;
  saveLabel?: string;
  sidebar?: boolean;
  sidebarContent?: ReactNode;
  sidebarCollapsed?: boolean;
  onToggleSidebarCollapse?: () => void;
  sidebarTitle?: string | null;
  sidebarToolsContent?: ReactNode;
  measuredAreaTools?: ReactNode;
  hideMeasurementTotals?: boolean;
}

export default function Controls({
  map,
  currentDrawShape,
  color,
  setColor,
  name,
  setName,
  isEditing,
  onSave,
  isDragging,
  toggleDrag,
  deleteLayer,
  total,
  showLineLengthsWhileMeasuring,
  setShowLineLengthsWhileMeasuring,
  showLineLength,
  setShowLineLength,
  showTooltip,
  setShowTooltip,
  editingLayer,
  editingLayerType,
  toggleDrawMode,
  isTouch,
  selectPlace,
  clearMap,
  createTextBox,
  textToolActive,
  selectedTextBox,
  updateSelectedTextBox,
  takeScreenshot,
  isGeneratingScreenshot,
  selectedAddress,
  showSave,
  hideLocation = false,
  goBack = false,
  goBackTo = "/",
  goBackLabel = "Go to Dashboard",
  showTools = ["LINEAR_FOOT", "SQUARE_FOOT", "SQUARE_YARD"],
  close,
  undoLastDrawPoint,
  canUndo,
  saveLabel = "Update Proposal",
  sidebar = false,
  sidebarContent,
  sidebarCollapsed = false,
  onToggleSidebarCollapse,
  sidebarTitle = null,
  sidebarToolsContent,
  measuredAreaTools,
  hideMeasurementTotals = false,
}: Props) {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const isMountedRef = useRef(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [sidebarToolsOpen, setSidebarToolsOpen] = useState(true);
  const isTextBoxEditing = editingLayerType === "text" && textToolActive;

  const normalizeColorInput = (value?: string) => {
    if (!value) return "#ffffff";
    const trimmed = value.trim();
    if (trimmed.startsWith("#") && (trimmed.length === 7 || trimmed.length === 4)) {
      return trimmed.length === 4
        ? `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
        : trimmed;
    }
    if (trimmed.startsWith("rgb")) {
      const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (match) {
        const r = Number(match[1]).toString(16).padStart(2, "0");
        const g = Number(match[2]).toString(16).padStart(2, "0");
        const b = Number(match[3]).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`;
      }
    }
    return "#ffffff";
  };

  // Helper function to format numbers with commas
  const formatNumberWithCommas = (num: number): string => {
    return Math.round(num).toLocaleString();
  };

  const options = {
    componentRestrictions: { country: ["USA", "CA"] },
    fields: ["address_components", "geometry"],
  };

  const fillAddressInput = () => {
    if (!inputRef.current || !selectedAddress?.line1) return;
    const parts = [
      selectedAddress.line1,
      selectedAddress.line2,
      [selectedAddress.city, selectedAddress.state].filter(Boolean).join(", "),
      selectedAddress.zip,
      selectedAddress.country,
    ].filter(Boolean);
    inputRef.current.value = parts.join(", ");
  };
  const fillAddressInputRef = useRef(fillAddressInput);
  fillAddressInputRef.current = fillAddressInput;

  useEffect(() => {
    fillAddressInputRef.current();
  }, [selectedAddress]);

  useEffect(() => {
    if (!map) {
      return;
    }

    isMountedRef.current = true;
    let checkGoogleMaps: ReturnType<typeof setInterval> | null = null;

    const cleanupAutocomplete = () => {
      if (autoCompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autoCompleteRef.current);
      }
      autoCompleteRef.current = null;
    };

    function initializeAutocomplete() {
      if (!inputRef.current) return;
      if (!window.google?.maps?.places?.Autocomplete) return;

      cleanupAutocomplete();

      autoCompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      fillAddressInputRef.current();

      autoCompleteRef.current.addListener("place_changed", async function () {
        if (!isMountedRef.current || !autoCompleteRef.current) {
          return;
        }
        const place = await autoCompleteRef.current.getPlace();
        if (place?.name) {
          alert("Invalid address");
          return;
        }
        if (!inputRef.current) {
          return;
        }
        inputRef.current.value = "";

        selectPlace(place);
      });
    }

    if (!window.google?.maps?.places) {
      checkGoogleMaps = setInterval(() => {
        if (window.google?.maps?.places) {
          if (checkGoogleMaps) {
            clearInterval(checkGoogleMaps);
            checkGoogleMaps = null;
          }
          initializeAutocomplete();
        }
      }, 100);
    } else {
      initializeAutocomplete();
    }

    return () => {
      isMountedRef.current = false;
      if (checkGoogleMaps) {
        clearInterval(checkGoogleMaps);
      }
      cleanupAutocomplete();
    };
  }, [map]);

  const topControlsContent = (
    <>
      {/* Location Input - Hidden when hideLocation is true */}
      {!hideLocation && (
        <div className="location-input-section">
          <input ref={inputRef} className="location-input" placeholder="Search location..." />
          {selectedAddress && (
            <div className="selected-address">
              {selectedAddress.line1}
              {selectedAddress.line2 ? `, ${selectedAddress.line2}` : ""}, {selectedAddress.city},{" "}
              {selectedAddress.state}, {selectedAddress.zip}, {selectedAddress.country}
            </div>
          )}
        </div>
      )}

      <div className="toolbar">
        <button
          title={`${currentDrawShape === "Polygon" ? "Disable" : "Enable"} Polygon mode`}
          className={`toolbar-btn ${currentDrawShape === "Polygon" ? "active" : ""}`}
          onClick={() => toggleDrawMode(map, "Polygon", color, isEditing, currentDrawShape)}
        >
          <img src={Polygon} alt="Polygon Icon" />
        </button>
        <button
          title={`${currentDrawShape === "Marker" ? "Disable" : "Enable"} Marker mode`}
          className={`toolbar-btn ${currentDrawShape === "Marker" ? "active" : ""}`}
          onClick={() => toggleDrawMode(map, "Marker", color, isEditing, currentDrawShape)}
        >
          <img src={Marker} alt="Map Marker Icon" />
        </button>
        <button
          title={`${currentDrawShape === "Line" ? "Disable" : "Enable"} Line mode`}
          className={`toolbar-btn ${currentDrawShape === "Line" ? "active" : ""}`}
          onClick={() => toggleDrawMode(map, "Line", color, isEditing, currentDrawShape)}
        >
          <img src={perimeter} alt="Perimeter" width={16} height={16} />
        </button>
        <button
          title={`${currentDrawShape === "FreePolygon" ? "Disable" : "Enable"} Free Draw Area mode${
            isTouch ? " - Single finger to draw, tap to finish" : ""
          }`}
          className={`toolbar-btn ${
            currentDrawShape === "FreePolygon" ? "active free-draw-active" : ""
          }`}
          onClick={() => toggleDrawMode(map, "FreePolygon", color, isEditing, currentDrawShape)}
        >
          <img src={freeDrawPolygon} alt="Free Draw Polygon Icon" width={16} height={16} />
        </button>
        <button
          title={`${
            currentDrawShape === "FreePolyline" ? "Disable" : "Enable"
          } Free Draw Perimeter mode${isTouch ? " - Single finger to draw, tap to finish" : ""}`}
          className={`toolbar-btn ${
            currentDrawShape === "FreePolyline" ? "active free-draw-active" : ""
          }`}
          onClick={() => toggleDrawMode(map, "FreePolyline", color, isEditing, currentDrawShape)}
        >
          <img src={freeDrawPerimeter} alt="Free Draw Perimeter Icon" width={16} height={16} />
        </button>
        <button
          title="Add text box"
          className={`toolbar-btn ${textToolActive ? "active" : ""}`}
          onClick={createTextBox}
        >
          <Icon fontSize="small" color="inherit">
            text_fields
          </Icon>
        </button>
        <button
          title={canUndo ? "Undo last point" : "Undo (only available when drawing)"}
          className={`toolbar-btn ${canUndo ? "" : "disabled"}`}
          onClick={canUndo ? undoLastDrawPoint : undefined}
          disabled={!canUndo}
        >
          <img src={undo} alt="Delete Map Icon" width={16} height={16} />
        </button>
        {isEditing && editingLayerType != "label" && (
          <button title="Delete layer" className="toolbar-btn delete-btn" onClick={deleteLayer}>
            <Icon fontSize="small" color="inherit">
              delete
            </Icon>
          </button>
        )}
        {editingLayerType != "label" && !isTextBoxEditing && (
          <button
            className="toolbar-btn current-color-swatch-button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            style={{ backgroundColor: color }}
            title={`${color} - Click to ${showColorPicker ? "hide" : "show"} color picker`}
          >
            <div className="current-color-swatch" />
            <Icon fontSize="small" className="chevron-icon">
              {showColorPicker ? "keyboard_arrow_up" : "keyboard_arrow_down"}
            </Icon>
          </button>
        )}
        <button title="Clear map" className="toolbar-btn" onClick={clearMap}>
          <img src={clearMapIcon} alt="Delete Map Icon" width={16} height={16} />
        </button>
        <button
          title={
            isGeneratingScreenshot ? "Generating screenshot with map tiles..." : "Take screenshot"
          }
          className={`toolbar-btn ${isGeneratingScreenshot ? "disabled" : ""}`}
          onClick={async () => {
            if (isGeneratingScreenshot) return;
            try {
              await takeScreenshot();
            } catch (error) {
              console.error("Screenshot button error:", error);
            }
          }}
          disabled={isGeneratingScreenshot}
        >
          <Icon fontSize="small" color="inherit">
            {isGeneratingScreenshot ? "hourglass_top" : "camera_alt"}
          </Icon>
        </button>
        {measuredAreaTools}
        {sidebarToolsContent}
      </div>

      {(currentDrawShape === "Polygon" || currentDrawShape === "Line") && !isEditing && (
        <div className="checkbox-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showLineLengthsWhileMeasuring}
              onChange={() => setShowLineLengthsWhileMeasuring(!showLineLengthsWhileMeasuring)}
              className="checkbox-input"
            />
            Show line lengths while measuring
          </label>
        </div>
      )}

      {isEditing && !isTextBoxEditing && (
        <div className="area-description-section">
          <input
            type="text"
            placeholder="Area Description"
            value={name || ""}
            onChange={(e) => setName(e.target.value)}
            onKeyUp={(e) => e.stopPropagation()}
            className="area-description-input"
          />
        </div>
      )}

      {isEditing && !isTextBoxEditing && !(editingLayer.layer instanceof L.Marker) && (
        <div className="checkbox-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showLineLength}
              onChange={() => setShowLineLength(!showLineLength)}
              className="checkbox-input"
            />
            Show line length
          </label>
        </div>
      )}

      {isEditing && !isTextBoxEditing && (
        <div className="checkbox-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showTooltip}
              onChange={() => setShowTooltip(!showTooltip)}
              className="checkbox-input"
            />
            Show area box
          </label>
        </div>
      )}

      {editingLayerType != "label" && !isTextBoxEditing && showColorPicker && (
        <div className="color-picker-section">
          <CirclePicker
            color={color}
            onChangeComplete={(color) => {
              setColor(color.hex);
            }}
            circleSize={16}
            circleSpacing={5}
          />
        </div>
      )}

      {isTextBoxEditing && selectedTextBox && (
        <div className="text-box-controls">
          <div className="text-box-control">
            <label className="text-box-label">Font size</label>
            <input
              type="number"
              min={10}
              max={48}
              value={selectedTextBox.fontSize}
              onChange={(e) => updateSelectedTextBox({ fontSize: Number(e.target.value || 14) })}
              className="text-box-input"
            />
          </div>
          <div className="text-box-control">
            <label className="text-box-label">Font color</label>
            <input
              type="color"
              value={selectedTextBox.fontColor}
              onChange={(e) => updateSelectedTextBox({ fontColor: e.target.value })}
              className="text-box-color-input"
            />
          </div>
          <div className="text-box-control">
            <label className="text-box-label">Background</label>
            <input
              type="color"
              value={normalizeColorInput(selectedTextBox.backgroundColor)}
              onChange={(e) => updateSelectedTextBox({ backgroundColor: e.target.value })}
              className="text-box-color-input"
            />
          </div>
        </div>
      )}

      {!hideMeasurementTotals && (
        <div className="measurements-section">
          <div className="measurement-row">
            <span className="measurement-label">Total Area:</span>
            <span className="measurement-value">
              {formatNumberWithCommas(total.area.feet)}ft² /{" "}
              {formatNumberWithCommas(total.area.yards)}
              yd²
            </span>
          </div>
          <div className="measurement-row">
            <span className="measurement-label">Total Perimeter:</span>
            <span className="measurement-value">
              {formatNumberWithCommas(total.perimeter.feet)}ft /{" "}
              {formatNumberWithCommas(total.perimeter.yards)}yd
            </span>
          </div>
        </div>
      )}

      {isTouch && isEditing && (
        <div className="touch-controls">
          {(editingLayerType == "label" || editingLayer.layer instanceof L.Marker) && (
            <button onClick={toggleDrag} className="touch-control-btn">
              {isDragging ? "Stop Drag" : "Drag"}
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <div
        className={`new-map-controls-container${sidebar ? " sidebar" : ""}${
          sidebar && sidebarCollapsed ? " sidebar-collapsed" : ""
        }`}
      >
        {sidebar && sidebarCollapsed && (
          <button
            className="sidebar-collapse-toggle"
            title="Expand"
            onClick={onToggleSidebarCollapse}
          >
            <Icon fontSize="small" color="inherit">
              chevron_right
            </Icon>
          </button>
        )}
        {!(sidebar && sidebarCollapsed) && (
          <>
            {sidebar ? (
              <div className="sidebar-tools-drawer">
                <div className="sidebar-tools-drawer-header">
                  <button
                    className="sidebar-tools-drawer-title-button"
                    onClick={() => setSidebarToolsOpen((open) => !open)}
                  ></button>
                  <button
                    className="sidebar-tools-drawer-icon-button"
                    title="Collapse sidebar"
                    onClick={onToggleSidebarCollapse}
                  >
                    <Icon fontSize="small">chevron_left</Icon>
                  </button>
                  <span className="sidebar-tools-drawer-spacer" />
                  <button
                    className="sidebar-tools-drawer-icon-button"
                    title={sidebarToolsOpen ? "Collapse tools" : "Expand tools"}
                    onClick={() => setSidebarToolsOpen((open) => !open)}
                  >
                    <span
                      className={`sidebar-tools-drawer-chevron ${sidebarToolsOpen ? "open" : ""}`}
                    >
                      ▾
                    </span>
                  </button>
                </div>
                {sidebarToolsOpen && (
                  <div className="sidebar-tools-drawer-body">{topControlsContent}</div>
                )}
              </div>
            ) : (
              topControlsContent
            )}

            {sidebar && sidebarContent && (
              <div className="ai-sidebar-section" data-testid="ai-mapping-sidebar">
                {sidebarContent}
              </div>
            )}

            <div className="action-buttons">
              {showSave && (
                <button className="action-btn primary" onClick={onSave}>
                  {saveLabel}
                </button>
              )}
              {!goBack && close && (
                <button className="action-btn secondary" onClick={close}>
                  Back to Proposal
                </button>
              )}
              {goBack && (
                <Link to={goBackTo} className="action-btn-link">
                  <button className="action-btn secondary">{goBackLabel}</button>
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
